/**
 * Manual Surface Host Tool
 *
 * Standalone host that draws surfaces the "old way" — click-to-place points via
 * the `MeasureSubTool` (MeasureEngine) — paired with a `SurfaceKindAdapter` so
 * the drawn polygon commits through the surface `measurement_finish` path.
 * Restores the former `ManualSurfaceDrawTool` as a flat sibling toolbar entry.
 *
 * Keeps the surface isolation + camera-align it inherits from
 * `SurfaceToolBase`; camera alignment is Ctrl-gated like `SurfaceHostTool` so a
 * plain click places a point instead of rotating the view.
 */

import { SurfaceToolBase } from './SurfaceToolBase';
import { Viewer } from '../../../viewer/viewer';
import { IPersona } from '../../types';
import { PointoramaSurface } from '../../../utils/PointoramaSurface';
import { SubToolHosting } from '../../SubToolHosting';
import { SurfaceAlignHosting } from './SurfaceAlignHosting';
import { MeasureSubTool } from './MeasureSubTool';
import { SurfaceKindAdapter } from './SurfaceKindAdapter';

export class ManualSurfaceHostTool extends SurfaceAlignHosting(SubToolHosting(SurfaceToolBase)) {
  readonly name = 'manual_surface_draw';

  private readonly surfaceAdapter: SurfaceKindAdapter;

  constructor(viewer: Viewer, persona: IPersona) {
    super(viewer, persona);
    this.surfaceAdapter = new SurfaceKindAdapter();
    const subtool = new MeasureSubTool(viewer, persona, this.surfaceAdapter);
    const ctx = this.surfaceAdapter.createSelectionContext(viewer, persona);
    this.initSubToolHosting(subtool, this.surfaceAdapter, ctx);
  }

  // Lifecycle (onEnable/onDisable/dispose) is owned by SubToolHosting.

  protected onSurfaceChanged(_surface: PointoramaSurface | null, _identifier: string | null): void {
    this.surfaceAdapter.refreshForSelection();
    this.pushBasePlane();
  }

  protected onViewChanged(): void {
    this.pushBasePlane();
  }

  // Ctrl-gated camera alignment intercepts come from SurfaceAlignHosting.

  onClick(event: MouseEvent): void {
    if (this.isCameraAlignmentActive()) return;
    super.onClick(event);
  }
}
